import { BetterStackBackend } from "./backends/betterstack.js";
import { ConsoleBackend } from "./backends/console.js";
/**
 * Unified logger implementation
 * Provides structured logging with support for multiple backends
 *
 * Usage:
 * ```typescript
 * import { logger } from "@agent-todo/shared"
 *
 * // Initialize in your worker/app entry point
 * logger.initialize({
 *   serviceName: "agent-gate",
 *   betterStackToken: env.BETTER_STACK_SOURCE_TOKEN,
 *   environment: env.ENVIRONMENT,
 * })
 *
 * // Log messages with context
 * logger.info("User logged in", { userId: user.id, email: user.email })
 * logger.error("Failed to process request", error, { operation: "mcp-call" })
 *
 * // Log performance metrics
 * logger.performance({ operation: "api.tasks.create", duration: 45, metadata: { userId: "123" } })
 *
 * // Create child logger with inherited context
 * const requestLogger = logger.withContext({ requestId: "req-123" })
 * requestLogger.info("Processing request") // Automatically includes requestId
 * ```
 */
export class UnifiedLogger {
    static instance = null;
    backends = [];
    serviceName = "unknown";
    environment = "production";
    inheritedContext = {};
    /**
     * Get singleton instance
     */
    static getInstance() {
        UnifiedLogger.instance ??= new UnifiedLogger();
        return UnifiedLogger.instance;
    }
    /**
     * Initialize logger with configuration
     * Should be called once at application startup
     */
    initialize(config) {
        this.serviceName = config.serviceName;
        this.environment = config.environment ?? "production";
        // Clear existing backends
        this.backends = [];
        // Add BetterStack backend for server-side logging
        if (config.betterStackToken) {
            this.backends.push(new BetterStackBackend(config.betterStackToken, config.betterStackEndpoint));
        }
        // Always add console backend as fallback
        this.backends.push(new ConsoleBackend());
    }
    /**
     * Whether initialize() has run. A fresh logger has no backends, so it drops every message.
     */
    isInitialized() {
        return this.backends.length > 0;
    }
    /**
     * Attach the current event's ExecutionContext to the BetterStack backend.
     * Workers can cancel pending log requests when a handler returns unless they
     * are registered with waitUntil, so call this at the start of every event
     * (fetch, scheduled, queue, email).
     */
    setExecutionContext(ctx) {
        this.backends.forEach((backend) => {
            if (backend instanceof BetterStackBackend) {
                backend.setContext(ctx);
            }
        });
    }
    /**
     * Format a log entry with standard fields
     */
    formatLog(level, message, context) {
        const entry = {
            dt: new Date().toISOString(),
            level: level.toUpperCase(),
            message,
            service: this.serviceName,
            environment: this.environment,
        };
        // Merge inherited context first
        Object.entries(this.inheritedContext).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                entry[key] = value;
            }
        });
        // Then add/override with provided context
        if (context) {
            Object.entries(context).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    entry[key] = value;
                }
            });
        }
        return entry;
    }
    /**
     * Send log entry to all backends
     * Non-blocking to avoid impacting application performance
     */
    send(entry) {
        this.backends.forEach((backend) => {
            // Wrap in void to explicitly ignore promises
            void Promise.resolve(backend.send(entry)).catch(() => {
                // Silently fail individual backends
            });
        });
    }
    /**
     * Log debug message
     * Only logs in development environment
     */
    debug(message, context) {
        // Only log debug in non-production
        if (this.environment !== "production") {
            this.send(this.formatLog("debug", message, context));
        }
    }
    /**
     * Log info message
     */
    info(message, context) {
        this.send(this.formatLog("info", message, context));
    }
    /**
     * Log warning message
     */
    warn(message, context) {
        this.send(this.formatLog("warn", message, context));
    }
    /**
     * Log error message with optional error object
     */
    error(message, error, context) {
        const errorContext = { ...context };
        // Handle error objects specially
        if (error instanceof Error) {
            errorContext["error"] = error.message;
            if (error.stack) {
                errorContext["stack"] = error.stack;
            }
        }
        else if (error) {
            errorContext["error"] = String(error);
        }
        this.send(this.formatLog("error", message, errorContext));
    }
    /**
     * Log performance metric
     */
    performance(metric) {
        const context = {
            operation: metric.operation,
            duration_ms: metric.duration,
            ...metric.metadata,
        };
        this.send(this.formatLog("info", `Performance: ${metric.operation}`, context));
    }
    /**
     * Create a child logger with inherited context
     * The child logger will automatically include the provided context in all log messages
     */
    withContext(context) {
        const child = new UnifiedLogger();
        child.backends = this.backends;
        child.serviceName = this.serviceName;
        child.environment = this.environment;
        child.inheritedContext = { ...this.inheritedContext, ...context };
        return child;
    }
    /**
     * Flush any pending logs
     * Important for serverless environments to ensure all logs are sent before shutdown
     */
    async flush() {
        // Wait for all backends to complete their pending operations
        await Promise.allSettled(this.backends.map(async (backend) => {
            // If backend has a flush method, call it
            if ("flush" in backend && typeof backend.flush === "function") {
                await backend.flush();
            }
        }));
    }
    /**
     * Get backend configuration status
     * Used for health checks and startup validation
     */
    getBackendStatus() {
        return {
            backendCount: this.backends.length,
            hasBetterStack: this.backends.some((b) => b instanceof BetterStackBackend),
            hasConsole: this.backends.some((b) => b instanceof ConsoleBackend),
            serviceName: this.serviceName,
            environment: this.environment,
        };
    }
}
/**
 * Singleton logger instance
 * Import and use this in your application
 */
export const logger = UnifiedLogger.getInstance();
//# sourceMappingURL=logger.js.map